import type { QualityTier } from "../state/viewerStore";

interface GpuInfo {
  vendor: string;
  renderer: string;
}

function readGpuInfo(): GpuInfo | null {
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl2") ?? canvas.getContext("webgl");
    if (!gl) return null;
    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    if (!ext) return null;
    return {
      vendor: gl.getParameter(ext.UNMASKED_VENDOR_WEBGL) as string,
      renderer: gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) as string,
    };
  } catch {
    return null;
  }
}

export function detectInitialQuality(): QualityTier {
  const ua = navigator.userAgent;
  const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
  const cores = navigator.hardwareConcurrency ?? 4;
  const mem = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 4;
  const gpu = readGpuInfo();
  const r = gpu?.renderer?.toLowerCase() ?? "";

  // Desktop discrete GPU → ultra
  if (!isMobile && /(rtx|radeon rx|geforce|quadro)/.test(r)) return "ultra";
  // Apple Silicon / high-end mobile GPU
  if (isMobile && /(apple m|apple a1[5-9]|apple a2[0-9]|adreno [7-9])/.test(r)) return "high";
  // High core count + RAM, no specific GPU info → assume desktop integrated
  if (!isMobile && cores >= 8 && mem >= 8) return "high";
  if (isMobile && cores >= 6) return "medium";
  return "low";
}

export const QUALITY_TO_MAX_SPLATS: Record<QualityTier, number> = {
  low: 400_000,
  medium: 900_000,
  high: 2_000_000,
  ultra: 6_000_000,
};

export const QUALITY_TO_RESOLUTION_SCALE: Record<QualityTier, number> = {
  low: 0.65,
  medium: 0.85,
  high: 1.0,
  ultra: 1.0,
};
